<script setup>
import { useCartStore } from "@/stores/cart.store";
import { useProductsStore } from "@/stores/products.store";
import { useFastDeliveryCartStore } from "@/stores/fastDeliveryCart.store";
import { useMessageStore } from "@/stores/message.store";
import { canAddProductDuringSeason } from "@/helpers/season.js";
import { computed } from "vue";
import { trackAddToCart, trackRemoveFromCart } from '@/helpers/analytics.js'
import { productImageSrc } from '@/helpers/imageUrls.js'

// Props
const props = defineProps({
    productData: Object,
    isFastDelivery: {
        type: Boolean,
        default: false,
    },
    isSeasonal: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(["click"]);

const handleClick = () => {
    emit("click", props.productData);
};

const messageStore = useMessageStore();

const requiresProductDetails = computed(() => {
    if (props.isSeasonal) {
        return true;
    }
    return !!(
        props.productData?.variants?.[0]?.has_cut ||
        (props.productData?.variants?.length ?? 0) > 1
    );
});

// Cart functions
const productSearch = (id) => {
    if (props.isFastDelivery) {
        return useFastDeliveryCartStore().products.filter((product) => product.id === id).length;
    } else {
        return useCartStore().products.filter((product) => product.id === id).length;
    }
};

const addProductToCart = (product) => {
    if (props.isFastDelivery) {
        useFastDeliveryCartStore().addToCart(product);
        trackAddToCart(product, { cartType: 'fast' });
        useProductsStore().emptySearch();
    } else {
        const productsStore = useProductsStore();
        const seasonCheck = canAddProductDuringSeason(props.isSeasonal, productsStore.seasonDays);
        if (!seasonCheck.ok) {
            messageStore.messageControl = {
                show: true,
                message: seasonCheck.message,
                type: "warning",
            };
            return;
        }
        if (props.isSeasonal) {
            handleClick();
            return;
        }
        useCartStore().addToCart(product);
        trackAddToCart(product, { cartType: 'regular' });
        productsStore.emptySearch();
    }
};

const removeProductFromCart = (id) => {
    trackRemoveFromCart(props.productData, {
        quantity: 1,
        cartType: props.isFastDelivery ? 'fast' : 'regular',
    });
    if (props.isFastDelivery) {
        useFastDeliveryCartStore().removeFromCart(id);
    } else {
        useCartStore().removeFromCart(id);
    }
};


const isProductEligibleForFastCart = computed(() => {
    return !(props.isFastDelivery && props.productData.stock_qty === 0);
});
</script>
<template>
<div class="flex flex-col justify-start shrink-0 w-24 lg:w-[7.25rem] xl:w-32 2xl:w-36">

    <div @click="isProductEligibleForFastCart ? handleClick() : null"
        class="flex flex-col justify-start items-center self-stretch flex-grow-0 flex-shrink-0 relative gap-0.5">
        <!-- Semi-transparent layer for ineligible products -->
        <div v-if="!isProductEligibleForFastCart"
            class="absolute inset-0 bg-black bg-opacity-50 rounded-2xl z-10 pointer-events-none">
        </div>
        <!-- Label for out-of-stock products -->
        <div v-if="isFastDelivery && productData.stock_qty === 0"
            class="absolute inset-x-0 top-0 bg-red-500 text-white text-sm font-bold px-2 py-2 rounded-tl-lg rounded-tr-lg z-20">
            نفذت الكمية
        </div>
        <img class="self-stretch flex-grow-0 flex-shrink-0 h-[104px] lg:h-[118px] xl:h-[128px] 2xl:h-[140px] rounded-2xl z-0 pointer-events-none w-full object-cover"
            :src="productImageSrc(productData)"
            :alt="productData.name || 'منتج'"
            width="280"
            height="140"
            loading="lazy"
            decoding="async"
            draggable="false" />
        <!-- Buttons div moved and positioned absolutely at the bottom right -->
        <div id="buttons" class="absolute bottom-0 right-0 z-30">
            <!-- Plus button for adding to cart: only if single variant and not has_cut -->
            <div class="cursor-pointer"
                v-if="isProductEligibleForFastCart && !requiresProductDetails && productSearch(productData.id) == 0">
                <button type="button"
                    class="border p-1 w-10 h-10 rounded-full flex items-center justify-center bg-white/70"
                    :aria-label="'إضافة ' + (productData.name || 'المنتج') + ' للسلة'"
                    @click.stop="addProductToCart(productData)">
                    <font-awesome-icon class="text-xs" :icon="['fas', 'plus']" aria-hidden="true" />
                </button>
            </div>

            <!-- إذا لم يكن has_cut موجودًا وكان المنتج في السلة -->
            <div v-else-if="productSearch(productData.id) > 0 && productData?.variants.length == 1">
                <div class="p-1 font-semibold text-center text-white rounded-full border border-tazej-text-tazej-lightGreen-primary text-xs bg-tazej-lightGreen-primary flex justify-center gap-2">
                    <button type="button"
                        class="border border-white p-1 w-5 h-5 rounded-full flex items-center justify-center"
                        :aria-label="'زيادة كمية ' + (productData.name || 'المنتج')"
                        @click.stop="addProductToCart(productData)">
                        <font-awesome-icon class="text-white text-xs" :icon="['fas', 'plus']" aria-hidden="true" />
                    </button>
                    <span class="font-semibold">{{ productSearch(productData.id) }}</span>
                    <button type="button"
                        class="border border-white p-1 w-5 h-5 rounded-full flex items-center justify-center"
                        :aria-label="'تقليل كمية ' + (productData.name || 'المنتج')"
                        @click.stop="removeProductFromCart(productData.id)">
                        <font-awesome-icon class="text-white text-xs" :icon="['fas', 'minus']" aria-hidden="true" />
                    </button>
                </div>
            </div>

            <!-- إذا كان has_cut موجودًا أو يوجد أكثر من variant -->
            <div v-else-if="isProductEligibleForFastCart && requiresProductDetails">
                <button type="button"
                    class="border p-1 w-10 h-10 rounded-full flex items-center justify-center bg-white/70"
                    :aria-label="'اختيار خيارات ' + (productData.name || 'المنتج')"
                    @click.stop="isProductEligibleForFastCart ? handleClick() : null">
                    <font-awesome-icon class="text-xs" :icon="['fas', 'plus']" aria-hidden="true" />
                </button>
            </div>
        </div>

    </div>
    <div class="font-medium text-base md:text-lg xl:text-lg 2xl:text-xl w-full max-w-[96px] lg:max-w-[7.25rem] xl:max-w-32 2xl:max-w-36 break-words text-center h-20 lg:h-[4.5rem] 2xl:min-h-[5rem]">{{ productData.name }}</div>
    <div class="w-full text-nowrap flex-col gap-1 text-xl font-semibold text-tazej-gray-400 h-12">
        <!-- عرض السعر المخفض أو السعر الأصلي -->
        <div class="flex justify-center w-full mt-2">
            <span class="font-semibold text-[#515151]">
                {{ ((productData.variants[0].list_price ?? 0) * 1.15).toFixed(2) + " ريال"}}
            </span>
        </div>

        <!-- عرض السعر الأصلي مع خط عليه إذا كان هناك خصم -->
        <div v-if="productData.discount_percent > 0" class="flex justify-center w-full">
            <span class="text-red-400 text-xl mb-2" style="display: inline-block; position: relative">
                <span style="
                position: absolute;
                width: 100%;
                height: 1px;
                background-color: red;
                top: 50%;
                left: 0;
                transform: rotate(-10deg);
              "></span>
                {{ (productData.minimum_price * 1.15).toFixed(2) + " ر.س" }}
            </span>
        </div>
    </div>

</div>
</template>
<style scoped>
.shadow-black {
    box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.8);
    /* Adjusted to ensure a darker shadow */
}

/* Semi-transparent layer for ineligible products */
.bg-black.bg-opacity-50 {
    pointer-events: none;
    /* Ensure the layer does not block interactions */
}

.pointer-events-none {
    pointer-events: none;
    /* Prevent interaction with the image */
}
</style>
